import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API_URL } from '../env';
import SCATimer from '../components/SCATimer';
import EndConsultationModal from '../components/EndConsultationModal';
import PushToTalkButton from '../components/PushToTalkButton';
import TTSSettingsPanel from '../components/TTSSettingsPanel';
import TTSSkipButton from '../components/TTSSkipButton';
import { speakSequence, skipAll, findBestVoice, getGenderAppropriateVoices } from '../utils/ttsService';
import { detectGenderFromProfile } from '../utils/genderUtils';
import { TTS_ENGINES } from '../constants/tts';
import toast from 'react-hot-toast';
import { parsePatientResponse, toConciseNonVerbal } from '../utils/patientResponseParser';

const SCASession = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const [phase, setPhase] = useState('reading'); // 'reading' | 'consultation' | 'ended'
    const [candidateMaterial, setCandidateMaterial] = useState('');
    const [patientProfile, setPatientProfile] = useState(''); // New state for TTS gender detection
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [showEndModal, setShowEndModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ttsSettings, setTtsSettings] = useState(() => {
        try {
            const saved = localStorage.getItem('gp-simulator-tts-settings');
            return saved ? JSON.parse(saved) : { mode: 'off', patientVoiceURI: null, narratorVoiceURI: null, rate: 1.0 };
        } catch {
            return { mode: 'off', patientVoiceURI: null, narratorVoiceURI: null, rate: 1.0 };
        }
    });
    const [isTtsPlaying, setIsTtsPlaying] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        api.getSCASession(sessionId)
            .then(res => {
                setCandidateMaterial(res.data.candidate_material);
                // After getting session, fetch scenario for full profile (for gender/TTS detection)
                return api.getSCAScenario(res.data.scenario_id);
            })
            .then(res => {
                if (res.data) setPatientProfile(res.data.patient_profile);
            })
            .catch(err => {
                console.error("Failed to load SCA session details", err);
                toast.error("Failed to load session details");
            });

        // Initialize TTS settings from global config
        api.getTTSConfig().then(res => {
            const gc = res.data;
            setTtsSettings(prev => ({
                ...prev,
                engine: gc.engine || TTS_ENGINES.BROWSER,
                autoGenderSelect: gc.auto_gender_select,
                patientVoiceId: gc.patient_voice_id,
                narratorVoiceId: gc.narrator_voice_id,
                rate: gc.default_rate || 1.0,
                mode: gc.default_narration || 'all'
            }));
        }).catch(err => {
            console.error("Failed to fetch global TTS config:", err);
            // Fallback to local defaults
            setTtsSettings(prev => ({ ...prev, engine: TTS_ENGINES.BROWSER }));
        });

        return () => {
            skipAll();
        };
    }, [sessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleReadingComplete = () => {
        setElapsedSeconds(0); // Fix: Reset elapsed for consultation phase
        setPhase('consultation');
        toast.success("Consultation phase started!");
    };

    const handleEndConsultation = async () => {
        setLoading(true);
        setPhase('ended');
        setShowEndModal(false);
        skipAll();

        try {
            await api.endSCASession(sessionId, messages, elapsedSeconds);
            await api.scoreSCASession(sessionId);
            navigate(`/sca/review/${sessionId}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to finalize session. Please wait.");
            navigate(`/sca/review/${sessionId}`);
        } finally {
            setLoading(false);
        }
    };

    const sendMessage = async (e, textOverride = null) => {
        if (e) e.preventDefault();
        const textToSend = textOverride !== null ? textOverride : input;

        if (!textToSend.trim() || loading || phase !== 'consultation') return;

        skipAll();
        const userMsg = { role: 'student', content: textToSend, timestamp: elapsedSeconds };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/sca/session/${sessionId}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userMsg.content,
                    history: messages,
                    elapsed_seconds: elapsedSeconds
                })
            });

            if (!response.ok) throw new Error('Network response error');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            // Add placeholder with timestamp
            setMessages(prev => [...prev, { role: 'patient', content: '', timestamp: elapsedSeconds }]);

            let done = false;
            let accumulatedText = "";
            let buffer = "";

            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                const lines = buffer.split('\n\n');
                buffer = lines.pop();

                let textMatched = false;
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.text) {
                                accumulatedText += data.text;
                                textMatched = true;
                            }
                        } catch (e) { }
                    }
                }

                if (textMatched) {
                    const currentText = accumulatedText;
                    setMessages(prev => {
                        const newMsgs = [...prev];
                        const last = newMsgs[newMsgs.length - 1];
                        if (last.role === 'patient') last.content = currentText;
                        return newMsgs;
                    });
                }
            }

            const { segments, displayText } = parsePatientResponse(accumulatedText);
            setMessages(prev => {
                const newMsgs = [...prev];
                const last = newMsgs[newMsgs.length - 1];
                if (last.role === 'patient') {
                    newMsgs[newMsgs.length - 1] = {
                        ...last,
                        content: displayText,
                        segments
                    };
                }
                return newMsgs;
            });

            if (ttsSettings.mode !== 'off') {
                const patientGender = detectGenderFromProfile(patientProfile);

                let pVoice = null;
                let nVoice = null;

                if (ttsSettings.autoGenderSelect) {
                    const { patientVoice, narratorVoice } = await getGenderAppropriateVoices(
                        ttsSettings.engine || TTS_ENGINES.BROWSER,
                        patientGender
                    );
                    pVoice = patientVoice;
                    nVoice = narratorVoice;
                } else {
                    pVoice = ttsSettings.engine === TTS_ENGINES.BROWSER ? ttsSettings.patientVoiceURI : ttsSettings.patientVoiceId;
                    nVoice = ttsSettings.engine === TTS_ENGINES.BROWSER ? ttsSettings.narratorVoiceURI : ttsSettings.narratorVoiceId;
                }

                // Fallback to auto-select if still null
                if (!pVoice) {
                    const best = await findBestVoice(patientGender, 'en-GB', { engine: ttsSettings.engine || TTS_ENGINES.BROWSER });
                    pVoice = best?.id || best?.voiceURI;
                }

                const ttsSegments = segments
                    .filter(seg => ttsSettings.mode === 'all' || seg.type === 'verbal')
                    .map(seg => ({
                        text: seg.tts,
                        voiceId: seg.type === 'verbal' ? pVoice : nVoice,
                        options: { rate: ttsSettings.rate || 1.0 }
                    }));

                if (ttsSegments.length > 0) {
                    setIsTtsPlaying(true);
                    try {
                        await speakSequence(ttsSegments);
                    } catch (err) {
                        console.error('Sequence playback failed:', err);
                    } finally {
                        setIsTtsPlaying(false);
                    }
                }
            }
        } catch (err) {
            console.error(err);
            setMessages(prev => [...prev, { role: 'system', content: 'Connection error.', timestamp: elapsedSeconds }]);
        } finally {
            setLoading(false);
        }
    };

    const renderMessageContent = (msg) => {
        if (msg.role !== 'patient') {
            return <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>;
        }

        if (!Array.isArray(msg.segments) || msg.segments.length === 0) {
            return <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>;
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                {msg.segments.map((segment, segmentIndex) => {
                    if (segment.type === 'nonverbal') {
                        const cueText = toConciseNonVerbal(segment.display || segment.tts);
                        if (!cueText) return null;
                        return (
                            <span
                                key={segmentIndex}
                                style={{
                                    color: '#2e7d32',
                                    fontStyle: 'italic',
                                    fontSize: '13px',
                                    lineHeight: 1.4
                                }}
                            >
                                [{cueText}]
                            </span>
                        );
                    }

                    return (
                        <span
                            key={segmentIndex}
                            style={{
                                color: '#0b5fff',
                                fontWeight: 700,
                                background: '#eaf2ff',
                                borderLeft: '4px solid #0b5fff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                lineHeight: 1.45
                            }}
                        >
                            {segment.display}
                        </span>
                    );
                })}
            </div>
        );
    };

    if (phase === 'ended') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <div className="spinner" style={{ marginBottom: '20px' }}></div>
                <h2>Analyzing Consultation...</h2>
                <p>Please wait while we generate your SCA domain marks and feedback.</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '90vh', maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
            <div className="card-bg" style={{ padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ margin: 0 }}>SCA Examination</h2>
                    <p style={{ margin: 0, fontSize: '14px' }} className="text-muted">
                        Phase: {phase === 'reading' ? 'Reading Time (3 mins)' : 'Consultation (12 mins)'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <SCATimer
                        durationSeconds={phase === 'reading' ? 180 : 720}
                        onTimeUp={phase === 'reading' ? handleReadingComplete : handleEndConsultation}
                        onTick={setElapsedSeconds}
                        isPaused={loading}
                        key={phase} // Reset timer when phase changes
                    />
                    {phase === 'consultation' && (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <TTSSkipButton
                                isPlaying={isTtsPlaying}
                                onSkip={() => {
                                    skipAll();
                                    setIsTtsPlaying(false);
                                }}
                            />
                            <button
                                className="btn btn-danger"
                                onClick={() => setShowEndModal(true)}
                                style={{ padding: '10px 20px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                End Consultation
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {phase === 'reading' ? (
                <div className="card-bg" style={{ flex: 1, padding: '40px', borderRadius: '12px', border: '1px solid var(--border-color)', overflowY: 'auto' }}>
                    <h2 style={{ borderBottom: '2px solid var(--primary-color)', paddingBottom: '10px' }}>Candidate Material</h2>
                    <div className="reading-content" dangerouslySetInnerHTML={{ __html: candidateMaterial }} />
                    <div style={{ marginTop: '50px', pearing: '20px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                        <p>Read the material carefully. The consultation will automatically start when the timer reaches zero.</p>
                        <button className="btn btn-primary" onClick={handleReadingComplete}>Start Consultation Now</button>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="secondary-bg" style={{ flex: 1, overflowY: 'auto', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                        {messages.length === 0 && <p className="text-muted" style={{ textAlign: 'center' }}>Introduce yourself to the patient to begin.</p>}
                        {messages.map((msg, idx) => (
                            <div key={idx} style={{ marginBottom: '15px', textAlign: msg.role === 'student' ? 'right' : 'left' }}>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '12px 18px',
                                    borderRadius: '12px',
                                    background: msg.role === 'student' ? '#0070f3' : 'white',
                                    color: msg.role === 'student' ? 'white' : 'black',
                                    maxWidth: '80%',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    border: msg.role === 'student' ? 'none' : '1px solid #eee'
                                }}>
                                    <strong>{msg.role === 'student' ? 'GP' : 'Patient'}: </strong>
                                    {renderMessageContent(msg)}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    <form onSubmit={sendMessage} className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', gap: '10px' }}>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask the patient something..."
                            className="input-bg"
                            style={{ flex: 1, padding: '12px', fontSize: '16px', borderRadius: '8px' }}
                            disabled={loading}
                        />
                        <PushToTalkButton onTranscriptComplete={(t) => sendMessage(null, t)} disabled={loading} apiUrl={API_URL} />
                        <button type="submit" disabled={loading} className="btn btn-primary" style={{ padding: '0 25px', borderRadius: '8px', background: '#0070f3', color: 'white', border: 'none' }}>Send</button>
                    </form>
                </div>
            )}

            <EndConsultationModal
                isOpen={showEndModal}
                onConfirm={handleEndConsultation}
                onCancel={() => setShowEndModal(false)}
                timeRemaining={720 - elapsedSeconds}
            />

            <div style={{ position: 'fixed', bottom: '20px', right: '20px' }}>
                <TTSSettingsPanel settings={ttsSettings} onSettingsChange={setTtsSettings} />
            </div>
        </div>
    );
};

export default SCASession;
